import { MATRIX_LABELS as SHARED_MATRIX_LABELS } from '@crm/shared'
import type { ParsedCustomsHint, RequestItemPriority } from '../../../shared/api/types'

// ─── Типы ─────────────────────────────────────────────────────────────────────

export const MATRIX_OPTIONS = SHARED_MATRIX_LABELS
export type MatrixOption = (typeof SHARED_MATRIX_LABELS)[number]

export interface ItemRow {
  /** Стабильный локальный id строки — нужен @dnd-kit для трекинга перестановок.
   *  Не путать с request_item.id из БД: uid живёт только в стейте формы. */
  uid: string
  partNumber: string
  /** Оригинальная строка заказчика (как прислал, без количества) — попадает в
   *  колонку «Артикул заказчика» в КП. Заполняется при парсинге, не меняется
   *  при ручной правке partNumber. Пусто у вручную добавленных строк. */
  customerPartNumber?: string
  brand: string
  quantity: number
  unit: string
  note: string
  priority: RequestItemPriority
  matrix: string
  /** Короткая категория-«наименование» из справочника номенклатуры (снапшот парсера). */
  category: string
  checked: boolean
  lineNumber: number | null
  /** Парсер не смог достоверно разобрать строку (пустой партномер/бренд/кол-во
   *  или warning от парсера). Строка подсвечивается красным, пользователь правит вручную. */
  needsManualReview?: boolean
  /** Причины, по которым парсер пометил строку как подозрительную — в tooltip. */
  parseWarnings?: string[]
  // ── Транзиентные подсказки парсера (не сохраняются, только подсветка/защита от дублей) ──
  namingStatus?: 'GREEN' | 'YELLOW' | 'RED'
  /** Откуда категория — для подсказки «почему жёлтое/красное». */
  namingSource?: 'catalog' | 'rules' | 'brand' | 'learned' | 'description' | null
  /** Канонический артикул из справочника, если ввод «грязный» (кнопка «подставить»). */
  dedupCanonical?: string | null
  dedupReason?: 'homoglyph' | 'spaces' | null
  /** Нетарифка из памяти по партномеру (Л3): ТН ВЭД и «Честный знак» из прошлых
   *  проверок логиста. `forPartNumber` — партномер, для которого пришла подсказка:
   *  после ручной правки артикула она больше не относится к строке и не показывается. */
  customsHint?: (ParsedCustomsHint & { forPartNumber: string }) | null
  /** Парсер заменил указанный бренд каталожным: оба варианта для выбора в ячейке
   *  «Бренд». `forPartNumber` — партномер, для которого пришла замена: после
   *  правки артикула выбор больше не относится к строке и не показывается. */
  brandChoice?: { catalog: string; input: string; forPartNumber: string } | null
}

export function makeRowUid(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `r${Date.now()}_${Math.random().toString(36).slice(2)}`
}

export function makeEmptyRow(): ItemRow {
  return {
    uid: makeRowUid(),
    partNumber: '',
    customerPartNumber: '',
    brand: '',
    quantity: 1,
    unit: 'pcs',
    note: '',
    priority: 'MEDIUM',
    matrix: '',
    category: '',
    checked: true,
    lineNumber: null,
    needsManualReview: false,
    parseWarnings: [],
  }
}

export const PRIORITY_LABELS: Record<RequestItemPriority, string> = {
  HIGH: '🔴 Высокий',
  MEDIUM: '🟡 Средний',
  LOW: '⚪ Базовый',
}

// Человекочитаемые подписи матриц — без эмодзи, в едином стиле CRM.
export const MATRIX_LABELS: Record<string, string> = {
  Склад: 'Склад',
  Китай: 'Китай',
  РФ: 'РФ',
  Производители: 'Производители',
  Стоки: 'Стоки',
}

// ─── Структура таблицы ────────────────────────────────────────────────────────

export const COL_ORDER = [
  'matrix',
  'partNumber',
  'category',
  'brand',
  'quantity',
  'unit',
  'note',
  'priority',
] as const
export type ColKey = (typeof COL_ORDER)[number]

export const DEFAULT_COL_WIDTHS: Record<ColKey, number> = {
  matrix: 110,
  partNumber: 200,
  category: 150,
  brand: 130,
  quantity: 70,
  unit: 58,
  note: 190,
  priority: 120,
}
